// Reversements (RECIPIENT_PAYOUT_API)
//
// Listing page for payouts initiated through the Moko Payout API.
// Gated by drc_merchant_services.RECIPIENT_PAYOUT_API; if not enabled,
// backend returns 403 and we surface an "Activate this service" hint.
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Send,
  Search,
  RefreshCcw,
  X,
  CalendarDays,
  Hash,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  Copy,
  User as UserIcon,
} from "lucide-react";
import {
  recipientPayoutService,
  RecipientPayoutItem,
  RecipientPayoutListParams,
} from "../services/recipientPayoutService";

const STATUSES = [
  "RESERVED",
  "HOLD_REVIEW",
  "DISPATCHED",
  "WAITING_CALLBACK",
  "COMPLETED",
  "FAILED",
  "RELEASED",
  "EXPIRED",
];

const METHODS = ["mpesa", "airtel", "orange", "afrimoney"];

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
  COMPLETED:        { bg: "bg-emerald-50",  text: "text-emerald-700", icon: <CheckCircle2 className="h-3.5 w-3.5" />, label: "Réussi" },
  FAILED:           { bg: "bg-rose-50",     text: "text-rose-700",    icon: <XCircle className="h-3.5 w-3.5" />,      label: "Échoué" },
  EXPIRED:          { bg: "bg-amber-50",    text: "text-amber-700",   icon: <Clock className="h-3.5 w-3.5" />,       label: "Expiré" },
  RELEASED:         { bg: "bg-slate-100",   text: "text-slate-700",   icon: <X className="h-3.5 w-3.5" />,           label: "Libéré" },
  WAITING_CALLBACK: { bg: "bg-blue-50",     text: "text-blue-700",    icon: <Clock className="h-3.5 w-3.5" />,       label: "En attente" },
  DISPATCHED:       { bg: "bg-indigo-50",   text: "text-indigo-700",  icon: <Clock className="h-3.5 w-3.5" />,       label: "Envoyé au switch" },
  RESERVED:         { bg: "bg-slate-50",    text: "text-slate-700",   icon: <Clock className="h-3.5 w-3.5" />,       label: "Réservé" },
  HOLD_REVIEW:      { bg: "bg-purple-50",   text: "text-purple-700",  icon: <AlertCircle className="h-3.5 w-3.5" />, label: "Revue requise" },
};

// Operator visual identity (colors loosely mapped to brand palettes — bg subtle, text saturated).
const OPERATOR_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  mpesa:     { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     label: "M-Pesa" },
  airtel:    { bg: "bg-rose-50",    text: "text-rose-700",    dot: "bg-rose-500",    label: "Airtel" },
  orange:    { bg: "bg-orange-50",  text: "text-orange-700",  dot: "bg-orange-500",  label: "Orange" },
  afrimoney: { bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-500",    label: "Afrimoney" },
};

const PAGE_SIZE = 25;

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || { bg: "bg-slate-100", text: "text-slate-700", icon: null, label: status };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.icon}
      {style.label}
    </span>
  );
}

function OperatorBadge({ method }: { method: string | null }) {
  if (!method) return <span className="text-slate-400 text-xs">—</span>;
  const m = method.toLowerCase();
  const style = OPERATOR_STYLES[m] || { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400", label: method };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
      {style.label}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDateParts(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch {
    return { date: iso, time: "" };
  }
}

function CopyableRef({ value, mono = true }: { value: string | null; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-slate-400 text-xs">—</span>;
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <span className="group inline-flex items-center gap-1.5 max-w-full">
      <span className={`${mono ? "font-mono" : ""} text-[12px] text-slate-700 truncate`} title={value}>{value}</span>
      <button
        onClick={handleCopy}
        className="opacity-0 group-hover:opacity-100 transition p-0.5 rounded hover:bg-slate-200 shrink-0"
        title={copied ? "Copié" : "Copier"}
      >
        {copied
          ? <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          : <Copy className="h-3 w-3 text-slate-500" />}
      </button>
    </span>
  );
}

function formatAmount(amount: number, currency: string): string {
  const fmt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
  return `${fmt.format(amount)} ${currency}`;
}

const RecipientPayouts: React.FC = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<RecipientPayoutItem | null>(null);

  const params: RecipientPayoutListParams = useMemo(() => {
    const p: RecipientPayoutListParams = { page, page_size: PAGE_SIZE };
    if (search.trim()) p.search = search.trim();
    if (statusFilter) p.status = statusFilter;
    if (methodFilter) p.method = methodFilter;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [search, statusFilter, methodFilter, dateFrom, dateTo, page]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["recipient-payouts", params],
    queryFn: () => recipientPayoutService.list(params),
    staleTime: 30 * 1000,
    retry: 1,
  });

  const accessDenied = (error as any)?.response?.status === 403;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const kpis = useMemo(() => {
    const items = data?.items || [];
    const completed = items.filter(i => i.status === "COMPLETED").length;
    const failed = items.filter(i => i.status === "FAILED").length;
    const inflight = items.filter(i => ["RESERVED", "DISPATCHED", "WAITING_CALLBACK", "HOLD_REVIEW"].includes(i.status)).length;
    return { total: data?.total || 0, completed, failed, inflight };
  }, [data]);

  // Access denied — show a clean hint instead of an error
  if (accessDenied) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 mb-4">
            <AlertCircle className="h-7 w-7 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            Reversements non activé pour votre compte
          </h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            Le service Reversements (API Moko Payout) n'est pas encore activé pour votre marchand.
            Contactez votre interlocuteur Moko pour l'activer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-blue-500/10 rounded-2xl p-6 lg:p-7 text-slate-800 shadow-lg border border-indigo-200/50 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm">
              <Send className="h-5 w-5 lg:h-6 lg:w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold mb-1 text-slate-800">Reversements</h1>
              <p className="text-slate-600 text-xs lg:text-sm">
                Paiements automatisés envoyés via l'API Moko Payout vers vos bénéficiaires pré-enregistrés
              </p>
            </div>
          </div>
          <div className="hidden lg:flex items-center space-x-2 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-emerald-700">API Moko Payout</span>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
            <p className="text-slate-500 text-xs font-medium">Total (page actuelle)</p>
            <p className="text-slate-900 font-semibold text-base">{kpis.total}</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
            <p className="text-slate-500 text-xs font-medium">Réussis (vue)</p>
            <p className="text-emerald-700 font-semibold text-base">{kpis.completed}</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
            <p className="text-slate-500 text-xs font-medium">En cours (vue)</p>
            <p className="text-indigo-700 font-semibold text-base">{kpis.inflight}</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
            <p className="text-slate-500 text-xs font-medium">Échoués (vue)</p>
            <p className="text-rose-700 font-semibold text-base">{kpis.failed}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs font-medium text-slate-600 block mb-1">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="PAY-… / réf marchande / numéro / telco ref"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Statut</label>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
            >
              <option value="">Tous</option>
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_STYLES[s]?.label || s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Opérateur</label>
            <select
              value={methodFilter}
              onChange={e => { setMethodFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
            >
              <option value="">Tous</option>
              {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Du</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Au</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <button
            onClick={() => { setSearch(""); setStatusFilter(""); setMethodFilter(""); setDateFrom(""); setDateTo(""); setPage(1); }}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition"
          >
            Réinitialiser
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 rounded-lg transition disabled:opacity-50"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] text-slate-500 uppercase tracking-wider sticky top-0">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Réf. Moko</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Réf. marchande</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Bénéficiaire</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Numéro</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Opérateur</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Montant</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Statut</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Description</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Réf. switch</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Réf. télécom</th>
                <th className="px-2 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading && (
                <tr><td colSpan={12} className="px-4 py-14 text-center text-slate-400 text-sm">Chargement…</td></tr>
              )}
              {!isLoading && isError && !accessDenied && (
                <tr><td colSpan={12} className="px-4 py-14 text-center text-rose-600 text-sm">Erreur lors du chargement</td></tr>
              )}
              {!isLoading && !isError && data?.items.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-16 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 mb-2">
                      <Search className="h-5 w-5 text-slate-400" />
                    </div>
                    <p className="text-sm text-slate-500">Aucun reversement trouvé pour ces critères</p>
                  </td>
                </tr>
              )}
              {data?.items.map(item => {
                const { date, time } = formatDateParts(item.created_at);
                return (
                  <tr
                    key={item.reference}
                    className="group hover:bg-indigo-50/40 transition cursor-pointer"
                    onClick={() => setSelected(item)}
                  >
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="text-slate-800 text-[13px] leading-tight">{date}</div>
                      <div className="text-[11px] text-slate-400 leading-tight mt-0.5">{time}</div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <CopyableRef value={item.reference} />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <CopyableRef value={item.merchant_reference} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <UserIcon className="h-3.5 w-3.5 text-slate-500" />
                        </div>
                        <span className="text-slate-800 text-[13px] truncate" title={item.recipient_name || ""}>
                          {item.recipient_name || <span className="text-slate-400">—</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[12px] text-slate-700">
                      {item.recipient_phone || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap"><OperatorBadge method={item.method} /></td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <div className="font-semibold text-slate-900 text-[13px] leading-tight">
                        {new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(item.amount)}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase leading-tight mt-0.5">{item.currency}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <StatusBadge status={item.status} />
                        {(item.status === "FAILED" || item.status === "EXPIRED") && item.rejected_reason && (
                          <span
                            className="text-[10.5px] text-rose-600 leading-snug max-w-[200px] truncate"
                            title={item.rejected_reason}
                          >
                            {item.rejected_reason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {item.description ? (
                        <span
                          className="text-[12px] text-slate-700 block max-w-[220px] truncate"
                          title={item.description}
                        >
                          {item.description}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <CopyableRef value={item.switch_reference} />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <CopyableRef value={item.telco_reference} />
                    </td>
                    <td className="px-2 py-2.5 text-right whitespace-nowrap">
                      <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-indigo-500 transition inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
            <p className="text-xs text-slate-600">
              Page {data.page} sur {totalPages} — {data.total} reversement{data.total > 1 ? "s" : ""} au total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-white"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-white"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer Detail */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
          <div
            className="relative ml-auto w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Détail du reversement</h2>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{selected.reference}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-slate-100">
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between">
                <StatusBadge status={selected.status} />
                <span className="text-lg font-bold text-slate-900">{formatAmount(selected.amount, selected.currency)}</span>
              </div>

              {(selected.status === "FAILED" || selected.status === "EXPIRED") && selected.rejected_reason && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-semibold text-rose-800 uppercase tracking-wider mb-1">
                        Raison de l'échec
                      </h3>
                      <p className="text-sm text-rose-900 break-words">{selected.rejected_reason}</p>
                    </div>
                  </div>
                </div>
              )}

              <Section title="Bénéficiaire">
                <Field label="Nom" value={selected.recipient_name} />
                <Field label="Numéro" value={selected.recipient_phone} mono />
                <Field label="Opérateur" value={selected.method} />
                <Field label="ID bénéficiaire" value={selected.recipient_id} mono />
              </Section>

              <Section title="Références">
                <Field label="Référence interne" value={selected.reference} mono icon={<Hash className="h-3.5 w-3.5" />} />
                <Field label="Référence marchande" value={selected.merchant_reference} mono icon={<Hash className="h-3.5 w-3.5" />} />
                <Field label="Switch reference" value={selected.switch_reference} mono />
                <Field label="Telecom reference" value={selected.telco_reference} mono />
              </Section>

              <Section title="Chronologie">
                <Field label="Créé le" value={formatDate(selected.created_at)} icon={<CalendarDays className="h-3.5 w-3.5" />} />
                <Field label="Finalisé le" value={formatDate(selected.completed_at)} icon={<CalendarDays className="h-3.5 w-3.5" />} />
              </Section>

              {selected.description && (
                <Section title="Description">
                  <p className="text-sm text-slate-700">{selected.description}</p>
                </Section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{title}</h3>
      <div className="space-y-2 bg-slate-50/50 border border-slate-200 rounded-lg p-3">
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, mono, icon }: { label: string; value: string | null | undefined; mono?: boolean; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-slate-500 flex items-center gap-1 shrink-0">{icon}{label}</span>
      <span className={`text-sm text-slate-800 text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

export default RecipientPayouts;
